import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  TextFieldProps,
  Grid,
  IconButton,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  MenuItem,
  Autocomplete,
  Checkbox,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { fetchApi } from '../services/api';
import CounselingRecordDialog from './CounselingRecordDialog';
import CloseIcon from '@mui/icons-material/Close';
import PaymentManageDialog from './PaymentManageDialog';
import { styled } from '@mui/material/styles';
import CostManageDialog from './CostManageDialog';
import InputAdornment from '@mui/material/InputAdornment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { is, ko } from 'date-fns/locale';
import PersonIcon from '@mui/icons-material/Person';
import DatePickerWrapper from '../components/DatePickerWrapper';
import ConfirmDialog from './ConfirmDialog';
import AssignInfoDialog from './AssignInfoDialog';
import { useMediaQuery } from '@mui/material';

interface CounselingDetail {
  견적경로: string;
  상담번호: number;
  상담구분: string;
  상담상태: string;
  조인업체명: string;
  담당자: string;
  담당자번호: number;
  담당자이메일: string;
  담당자전화: string;
  판매업체: string;
  판매업체번호: number;
  수배처: string | null;
  수배처번호: number | null;
  요청자명: string;
  요청자휴대폰: string;
  요청자이메일: string;
  일정표주소: string;
  시작일: string;
  종료일: string;
  견적명: string;
  견적내용: string;
  성인수: number;
  소아수: number;
  유아수: number;
  성인판매가: number;
  소아판매가: number;
  유아판매가: number;
  원가구분: string;
  통화코드: string;
  통화기호: string;
  성인원가: number;
  소아원가: number;
  유아원가: number;
  고객명단: string;
  인솔자수: number;
  입금총액: number;
  출금총액: number;
  총예상수익: number;
  총원가: CostDetail[];
  입출금: PaymentRecord[];
  총수익: number;
  첨부파일?: string[];
  판매총액: number;
  원가총액: number;
  고객번호?: number;  // 추가
}

interface PaymentRecord {
  입출금번호: number;
  일시: string;
  구분: '입금' | '출금' | '환불' | '환입';
  입출대상: string;
  내역: string;
  금액: number;
}

interface CostDetail {
  원가항목: string;
  통화기호: string;
  통화코드: string;
  환율적용방식: string;
  환율적용방식코드: string;
  적용환율: number;
  성인: number;
  어린이: number;
  영유아: number;
  공용원가여부: boolean;
  업체번호: number;
  판매가여부: boolean;
}

interface CompanyOption {
  코드: string;
  코드명: string;
}

interface CounselingDetailDialogProps {
  counselingNo: string;
  open: boolean;
  onClose: (refresh?: boolean) => void;
  initialData?: {
    요청자명?: string;
    요청자이메일?: string;
    요청자휴대폰?: string;
    고객번호?: number;
    수배처?: string;  // 추가
  };
}

interface AttachedFile {
  첨부파일: string;
  첨부파일경로: string;
}

interface CostManageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (personalCosts: CostDetail[], sharedCosts: CostDetail[]) => void;
  initialPersonalCosts?: CostDetail[];
  initialSharedCosts?: CostDetail[];
  counselingDetail: CounselingDetail | null;
  setCounselingDetail: (detail: CounselingDetail) => void;
  setIsDataModified: (modified: boolean) => void;
  isOtherCompanyData?: boolean;
  companyNo?: string | null;
}

interface AssignDialogProps {
  open: boolean;
  onClose: () => void;
  counselingDetail: CounselingDetail;
}

type CostDialogProps = CostManageDialogProps;

const StyledPaper = styled(Paper)`
  transition: box-shadow 0.1s ease-in-out;
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
  &:hover {
    outline: 0.5px solid #1976d2;
  }
`;

const BaseStyledTextField = styled(TextField)`
  width: 100%; // fullWidth 속성

  & .MuiOutlinedInput-root {
    height: 32px;
    font-size: 14px;
    background-color: #fff;
    &:hover fieldset {
      border-color: #1976d2;
    }

    &.Mui-focused fieldset {
      border-color: #1976d2;
    }

    &.Mui-disabled {
      background-color: #f5f5f5;
    }

    & input {
      text-align: left;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 4px 8px;
    text-align: left;
  }

  &.right-align .MuiOutlinedInput-input {
    text-align: right;
  }
` as typeof TextField;

// size="small"을 기본값으로 설정하기 위한 컴포넌트 래퍼
const StyledTextField = React.forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
  return <BaseStyledTextField {...props} size="small" fullWidth ref={ref} />;
});

// Date 객체를 포맷팅하는 함수
const formatDate = (date: Date): string => {
  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

// 시간 포맷팅 함수
const formatTime = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    console.error('Time formatting error:', error);
    return '';
  }
};

// 날짜 관련 함수들 수정
const parseDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr || dateStr === '') return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// 인터페이스 추가
interface CustomerOption {
  고객번호: number;
  고객명: string;
  이메일: string;
  대표전화: string;
}

// API 응답 타입 정의 추가
interface CustomerApiResponse {
  table: {
    고객번호: number;
    업체번호: number;
    고객명: string;
    성별: string;
    영문성: string;
    영문이름: string;
    대표전화: string;
    이메일: string;
    문자수신동의: boolean;
    이메일수신동의: boolean;
    상태코드: string;
    가입경로: string;
    최근접속일시: string;
    등록일시: string;
    변경일시: string;
    비고: string;
  }[];
}

// 인터페이스 추가
interface Supplier {
  업체번호: number;
  업체명: string;
}

const CounselingDetailDialog: React.FC<CounselingDetailDialogProps> = ({
  counselingNo,
  open,
  onClose,
  initialData
}) => {
  const [counselingDetail, setCounselingDetail] = useState<CounselingDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDataModified, setIsDataModified] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false); // 세부원가 다이얼로그 관련 상태 추가
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [personalCosts, setPersonalCosts] = useState<CostDetail[]>([]);
  const [sharedCosts, setSharedCosts] = useState<CostDetail[]>([]);
  const [records, setRecords] = useState<PaymentRecord[]>([]);
  const [판매총액, set판매총액] = useState(0);
  const [원가총액, set원가총액] = useState(0);
  const [성인수익, set성인수익] = useState(0);
  const [소아수익, set소아수익] = useState(0);
  const [유아수익, set유아수익] = useState(0);
  const [미수, set미수] = useState(0);
  const [미지급, set미지급] = useState(0);
  const [입금차액, set입금차액] = useState(0);
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  // const [teamColors, setTeamColors] = useState<string[]>([]);
  // const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [useDetailCost, setUseDetailCost] = useState<boolean>(true); // 401002 일때 TRUE
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [isOtherCompanyData, setIsOtherCompanyData] = useState(false);
  const [showAssignInfo, setShowAssignInfo] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  // const [searchTerm, setSearchTerm] = useState('');
  const [initialDisabled, setInitialDisabled] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  // 검색 필드 구분을 위한 상태 추가
  const [activeField, setActiveField] = useState<'고객명' | '대표전화' | null>(null);
  const [autoRegisterConfirmOpen, setAutoRegisterConfirmOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (!open) {
      setIsDataModified(false);
      setLoading(false);
      setCostDialogOpen(false);
      setPaymentDialogOpen(false);
      setSelectedTab(0);
      setCounselingDetail(null);
      setAttachedFiles([]);
      setIsDataModified(false);
      setLoading(false);

      // 계산된 값들 초기화
      set판매총액(0);
      set원가총액(0);
      set성인수익(0);
      set미수(0);
      set미지급(0);
      set입금차액(0);

      // 다이얼로그 상태 초기화
      setCostDialogOpen(false);
      setPaymentDialogOpen(false);
      setPersonalCosts([]);
      setSharedCosts([]);
      setRecords([]);

      // 탭 상태 초기화
      setSelectedTab(0);
      
      // 고객 관련 상태 초기화
      setCustomerOptions([]);
      setSelectedCustomer(null);
      
      // 날짜 초기화
      setDateRange([null, null]);
      
      // 기타 상태 초기화
      setIsOtherCompanyData(false);
      setShowAssignInfo(false);
      setInitialDisabled(false);
      setConfirmDialogOpen(false);
      setPendingClose(false);

      // 수배처 관련 상태 초기화 추가
      setSuppliers([]);
      setSelectedSupplier(null);
    }
  }, [open]);

  useEffect(() => {
    if (!counselingDetail) return;

    const companyNo = localStorage.getItem('CompanyNo');
    const isOther =
      companyNo !== counselingDetail.판매업체번호.toString() && companyNo === counselingDetail.수배처번호?.toString();
    setInitialDisabled(counselingDetail.수배처 !== null);
    setIsOtherCompanyData(isOther);

    // if (isOther) {
    //     setCounselingDetail(prev => ({
    //         ...prev!,
    //         요청자명: maskPersonalInfo(prev!.요청자명),
    //         요청자휴대폰: maskPersonalInfo(prev!.요청자자휴대폰폰),
    //         요청자이메일: maskPersonalInfo(prev!.요청자이메일)
    //     }));
    // }
  }, [counselingDetail]);

  // selectedCustomer가 변경될 때마다 고객번호 업데이트
  useEffect(() => {
    if (selectedCustomer?.고객번호) {
      if (initialData) {
        initialData.고객번호 = selectedCustomer.고객번호;
      }
    }
  }, [selectedCustomer]);

  const fetchCounselingData = async () => {
    if (!counselingNo || !open) return;
    setLoading(true);
    try {
      const detailResponse = await fetchApi<{ table: [{ 상담정보: string }] }>('DBO.ITN_S_상담상세', [
        { 컬럼명: '@업체번호', 값: localStorage.getItem('CompanyNo') },
        { 컬럼명: '@상담번호', 값: counselingNo },
        { 컬럼명: '@유저번호', 값: localStorage.getItem('UserNo') },
      ]);

      if (detailResponse.data.table?.[0]?.상담정보) {
        const parsedData = JSON.parse(detailResponse.data.table[0].상담정보)[0];
        setIsDataModified(false);


        // 신규 상담이고 initialData가 있는 경우
        if (counselingNo === '0' && initialData) {
          parsedData.요청자명 = initialData.요청자명 || '';
          parsedData.요청자이메일 = initialData.요청자이메일 || '';
          parsedData.요청자휴대폰 = initialData.요청자휴대폰 || '';
        }

        // 원가/판매가 재계산
        const loginCompanyNo = Number(localStorage.getItem('CompanyNo'));
        const salesPrice = parsedData.총원가.find((cost: any) => cost.판매가여부 && cost.업체번호 === loginCompanyNo);
        const personalCosts = parsedData.총원가.filter((cost: any) => !cost.공용원가여부 && cost.판매가여부 && cost.업체번호 != loginCompanyNo);

        // 개인 원가 계산
        const personalTotal = personalCosts.reduce((acc: any, cost: any) => ({
          성인: acc.성인 + (cost.성인 * cost.적용환율),
          소아: acc.소아 + (cost.어린이 * cost.적용환율),
          유아: acc.유아 + (cost.영유아 * cost.적용환율)
        }), { 성인: 0, 소아: 0, 유아: 0 });

        // 재계산된 원가와 판매가로 업데이트
        parsedData.성인원가 = Math.round(personalTotal.성인);
        parsedData.소아원가 = Math.round(personalTotal.소아);
        parsedData.유아원가 = Math.round(personalTotal.유아);
        parsedData.성인판매가 = Math.round(salesPrice ? salesPrice.성인 * salesPrice.적용환율 : 0);
        parsedData.소아판매가 = Math.round(salesPrice ? salesPrice.어린이 * salesPrice.적용환율 : 0);
        parsedData.유아판매가 = Math.round(salesPrice ? salesPrice.영유아 * salesPrice.적용환율 : 0);

        // 모든 계산을 위한 임시 변수들
        const 계산된판매총액 =
          parsedData.성인판매가 * parsedData.성인수 +
          parsedData.소아판매가 * parsedData.소아수 +
          parsedData.유아판매가 * parsedData.유아수;

        const 계산된원가총액 =
          parsedData.성인원가 * parsedData.성인수 +
          parsedData.소아원가 * parsedData.소아수 +
          parsedData.유아원가 * parsedData.유아수;

        const 계산된성인수익 = parsedData.성인판매가 - parsedData.성인원가;
        const 계산된소아수익 = parsedData.소아판매가 - parsedData.소아원가;
        const 계산된유아수익 = parsedData.유아판매가 - parsedData.유아원가;

        const 계산된미수 = 계산된판매총액 - parsedData.입금총액;
        const 계산된미지급 = 계산된원가총액 - parsedData.출금총액;
        const 계산된입금차액 = parsedData.입금총액 - parsedData.출금총액;

        // 계산된 값들 상태 업데이트
        set판매총액(계산된판매총액);
        set원가총액(계산된원가총액);
        set성인수익(계산된성인수익);
        set소아수익(계산된소아수익);
        set유아수익(계산된유아수익);
        set미수(계산된미수);
        set미지급(계산된미지급);
        set입금차액(계산된입금차액);
        setDateRange([new Date(parsedData.시작일), new Date(parsedData.종료일)]);

        // counselingDetail 업데이트
        setCounselingDetail({
          ...parsedData,
          총수익: 계산된판매총액 - 계산된원가총액 - parsedData.입금총액 + parsedData.출금총액,
          총예상수익: 계산된판매총액 - 계산된원가총액,
        });
        setAttachedFiles(parsedData.첨부파일리스트 || []);
        setRecords(parsedData.입출금 || []);
        setPersonalCosts((parsedData.총원가).filter((cost: any) => !cost.공용원가여부));
        setSharedCosts((parsedData.총원가).filter((cost: any) => cost.공용원가여부));

      }
    } catch (error) {
      console.error('상담 정보 조회 실패:', error);
      setCounselingDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounselingData();
  }, [counselingNo, open]);

  // 숫자 입력 처리를 위한 handleChange 함수
  const handleChange = (field: keyof CounselingDetail) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let processedValue = value;

    // 숫자 필드인 경우 콤마 제거 후 숫자로 변환
    if (
      [
        '성인수',
        '소아수',
        '유아수',
        '인솔자수',
        '성인판매가',
        '소아판매가',
        '유아판매가',
        '성인원가',
        '소아원가',
        '유아원가',
      ].includes(field)
    ) {
      const cleanValue = value.replace(/[^\d]/g, '');
      processedValue = cleanValue;
    }

    handleInputChange(field, processedValue);
  };

  const handleInputChange = (field: keyof CounselingDetail, value: any) => {
    if (!counselingDetail) return;
    let updatedValue = value;

    // 문자열로 처리해야 할 필드들
    const stringFields = [
      '견적경로',
      '상담구분',
      '상담상태',
      '조인업체명',
      '담당자',
      '판매업체',
      '수배처번호',
      '수배처',
      '요청자명',
      '요청자휴대폰',
      '요청자이메일',
      '일정표주소',
      '견적명',
      '견적내용',
      '고객명단',
      '시작일',
      '종료일',
    ];

    // 문자열 필드는 재계산 없이 직접 업데이트
    if (stringFields.includes(field)) {
      setCounselingDetail((prev) => ({
        ...prev!,
        [field]: value,
      }));
      setIsDataModified(true);
      return; // 재계산 없이 종료
    }

    // 숫자 필드는 기존 로직대로 처리
    if (typeof counselingDetail[field] === 'number') {
      updatedValue = Number(value) || 0;
    }

    const updatedDetail = {
      ...counselingDetail,
      [field]: updatedValue,
    };

    // 숫자 필드만 재계산
    const recalculatedDetail = recalculateAll(updatedDetail);
    setCounselingDetail(recalculatedDetail);
    setIsDataModified(true);
  };

  // 세부원가 계산 함수
  const calculateDetailCosts = (personalCosts: CostDetail[], sharedCosts: CostDetail[]) => {
    if (!counselingDetail) return null;

    // 개인별 원가 계산
    const 개인성인원가 = personalCosts.reduce((sum, cost) => sum + cost.성인 * cost.적용환율, 0);
    const 개인소아원가 = personalCosts.reduce((sum, cost) => sum + cost.어린이 * cost.적용환율, 0);
    const 개인유아원가 = personalCosts.reduce((sum, cost) => sum + cost.영유아 * cost.적용환율, 0);

    // 공용 원가 계산 (성인수로만 나누어 성인 원가에만 적용)
    const 성인수 = counselingDetail.성인수 || 1;
    const 공용원가 = sharedCosts.reduce((sum, cost) => sum + cost.성인 * cost.적용환율, 0);

    const 성인1인당공용원가 = 성인수 > 0 ? 공용원가 / 성인수 : 0;

    return {
      성인원가: Math.round(개인성인원가 + 성인1인당공용원가), // 성인원가에만 공용원가 포함
      소아원가: Math.round(개인소아원가), // 개인 원가만 적용
      유아원가: Math.round(개인유아원가), // 개인 원가만 적용
    };
  };

  // 재계산 함수
  const recalculateAll = (detail: CounselingDetail) => {
    const 계산된판매총액 =
      detail.성인판매가 * detail.성인수 + detail.소아판매가 * detail.소아수 + detail.유아판매가 * detail.유아수;

    const 계산된원가총액 =
      detail.성인원가 * detail.성인수 + detail.소아원가 * detail.소아수 + detail.유아원가 * detail.유아수;

    const 계산된성인수익 = detail.성인판매가 - detail.성인원가;
    const 계산된소아수익 = detail.소아판매가 - detail.소아원가;
    const 계산된유아수익 = detail.유아판매가 - detail.유아원가;

    const 계산된미수 = 계산된판매총액 - detail.입금총액;
    const 계산된미지급 = 계산된원가총액 - detail.출금총액;
    const 계산된입금차액 = detail.입금총액 - detail.출금총액;

    // 모든 상태 업데이트
    set판매총액(계산된판매총액);
    set원가총액(계산된원가총액);
    set성인수익(계산된성인수익);
    set소아수익(계산된소아수익);
    set유아수익(계산된유아수익);
    set미수(계산된미수);
    set미지급(계산된미지급);
    set입금차액(계산된입금차액);

    return {
      ...detail,
      판매총액: 계산된판매총액,
      원가총액: 계산된원가총액,
      총수익: 계산된판매총액 - 계산된원가총액,
      총예상수익: 계산된판매총액 - 계산된원가총액,
    };
  };

  // Utility functions
  const formatNumberWithCommas = (value: number | string | null | undefined) => {
    // null이나 undefined인 경우 빈 문자열 반환
    if (value === null || value === undefined) return '';

    // 빈 문자열인 경우 그대로 반환
    if (value === '') return '';

    const numStr = value.toString().replace(/[^\d-]/g, '');
    if (numStr === '' || numStr === '-') return '';

    // 숫자로 변환 가능한 경우만 포맷팅
    const num = Number(numStr);
    return isNaN(num) ? '' : num.toLocaleString('ko-KR');
  };

  // 고객번호 확인 함수 추가
  const checkCustomerNoAndSave = (newCustomerNo: number, retryCount: number = 0) => {
    if (retryCount >= 5) {
      console.error('고객번호 확인 실패');
      return;
    }

    // 고객번호가 모든 상태에 제대로 반영되었는지 확인
    const isCustomerNoSet = 
      initialData?.고객번호 === newCustomerNo && 
      selectedCustomer?.고객번호 === newCustomerNo && 
      (counselingDetail?.고객번호 === newCustomerNo || counselingDetail?.고객번호 === undefined);

    if (isCustomerNoSet) {
      handleSave(true);
    } else {
      setTimeout(() => {
        console.log('고객번호 확인 재시도:', retryCount + 1);
        checkCustomerNoAndSave(newCustomerNo, retryCount + 1);
      }, 500);
    }
  };

  // handleCustomerAutoRegister 함수 수정
  const handleCustomerAutoRegister = async () => {
    try {
      if (!counselingDetail) return;
      
      // 이미 고객번호가 있다면 중복 등록 방지
      if (initialData?.고객번호 || selectedCustomer?.고객번호) {
        handleSave(true);
        return;
      }
      
      const response = await fetchApi<CustomerApiResponse>('ITN_T_간편일정고객저장', [
        { "컬럼명": "@업체번호", "값": localStorage.getItem('CompanyNo') },
        { "컬럼명": "@고객번호", "값": "0" },
        { "컬럼명": "@이메일", "값": counselingDetail.요청자이메일 },
        { "컬럼명": "@고객명", "값": counselingDetail.요청자명 },
        { "컬럼명": "@대표전화", "값": counselingDetail.요청자휴대폰 },
        { "컬럼명": "@문자수신동의", "값": "0" },
        { "컬럼명": "@이메일수신동의", "값": "0" },
      ]);

      if (response.data?.table?.[0]?.고객번호) {
        const newCustomerNo = response.data.table[0].고객번호;

        // 새로운 상담 데이터 객체 생성
        const updatedCounselingDetail = {
          ...counselingDetail,
          고객번호: newCustomerNo
        };

        // 모든 상태를 한 번에 업데이트
        await Promise.all([
          new Promise<void>(resolve => {
            if (initialData) {
              initialData.고객번호 = newCustomerNo;
            }
            resolve();
          }),
          new Promise<void>(resolve => {
            setSelectedCustomer({
              고객번호: newCustomerNo,
              고객명: counselingDetail.요청자명,
              이메일: counselingDetail.요청자이메일,
              대표전화: counselingDetail.요청자휴대폰
            });
            resolve();
          }),
          new Promise<void>(resolve => {
            setCounselingDetail(updatedCounselingDetail);
            resolve();
          })
        ]);

        // 상담 저장 직접 실행
        const tempData = {
          ...updatedCounselingDetail,
          // ... 기존 tempData 필드들
        };

        const postData = [
          {
            컬럼명: '@상담정보',
            값: JSON.stringify(tempData),
          },
          {
            컬럼명: '@처리자',
            값: localStorage.getItem('UserNo'),
          },
          {
            컬럼명: '@업체번호',
            값: localStorage.getItem('CompanyNo'),
          },
          {
            컬럼명: '@고객번호',
            값: newCustomerNo
          },
          // ... 기타 필요한 postData
        ];

        const procedureName = isOtherCompanyData ? 'DBO.ITN_T_상담저장_수배처' : 'DBO.ITN_T_상담저장';
        
        const saveResponse = await fetchApi(procedureName, postData);

        if (saveResponse) {
          setAutoRegisterConfirmOpen(false);
          setIsDataModified(false);
          onClose(true);
        }
      }
    } catch (error) {
      console.error('Failed to register customer:', error);
    }
  };

  // handleSave 함수 수정
  const handleSave = async (skipCustomerCheck: boolean | React.MouseEvent = false) => {
    try {
      // MouseEvent인 경우 false로 처리
      const shouldSkipCheck = typeof skipCustomerCheck === 'boolean' ? skipCustomerCheck : false;

      // 고객번호 체크 로직 수정
      const hasCustomerNo = 
        initialData?.고객번호 || 
        selectedCustomer?.고객번호 || 
        counselingDetail?.고객번호;  // counselingDetail의 고객번호도 체크

      // 고객번호가 없고, skipCustomerCheck가 false일 때만 고객 체크 실행
      if (!shouldSkipCheck && !hasCustomerNo && 
          counselingDetail?.요청자명 && counselingDetail?.요청자휴대폰) {
        setAutoRegisterConfirmOpen(true);
        return;
      }

      // 제안 지상비 제외 로직 추가
      if (counselingDetail?.총원가) {
        const loginCompanyNo = Number(localStorage.getItem('CompanyNo'));
        counselingDetail.총원가 = counselingDetail.총원가.filter(cost => 
          !(cost.업체번호 !== loginCompanyNo)
        );
      }

      const tempData = {
        견적경로: counselingDetail?.견적경로 || '',
        상담번호: counselingDetail?.상담번호 || 0,
        상담구분: counselingDetail?.상담구분 || '',
        상담상태: counselingDetail?.상담상태 || '',
        조인업체명: counselingDetail?.조인업체명 || '',
        담당자: counselingDetail?.담당자 || '',
        담당자번호: counselingDetail?.담당자번호 || 0,
        판매업체: counselingDetail?.판매업체 || '',
        판매업체번호: counselingDetail?.판매업체번호 || 0,
        수배처: counselingDetail?.수배처,
        수배처번호: counselingDetail?.수배처번호,
        요청자명: counselingDetail?.요청자명 || '',
        요청자휴대폰: counselingDetail?.요청자휴대폰 || '',
        요청자이메일: counselingDetail?.요청자이메일 || '',
        시작일: counselingDetail?.시작일 || '',
        종료일: counselingDetail?.종료일 || '',
        견적명: counselingDetail?.견적명 || '',
        견적내용: counselingDetail?.견적내용 || '',
        고객명단: counselingDetail?.고객명단 || '',
        성인수: counselingDetail?.성인수 || 0,
        소아수: counselingDetail?.소아수 || 0,
        유아수: counselingDetail?.유아수 || 0,
        인솔자수: counselingDetail?.인솔자수 || 0,
        성인판매가: counselingDetail?.성인판매가 || 0,
        소아판매가: counselingDetail?.소아판매가 || 0,
        유아판매가: counselingDetail?.유아판매가 || 0,
        일정표주소: counselingDetail?.일정표주소 || '',
        첨부파일리스트: [],
        견적첨부파일리스트: [],

        통화코드: counselingDetail?.통화코드 || '',
        통화기호: counselingDetail?.통화기호 || '',
        원가구분: counselingDetail?.원가구분 || '',
        총원가: counselingDetail?.총원가 || [],
        세부원가: personalCosts.map((cost) => ({
          원가항목: cost.원가항목,
          통화기호: cost.통화기호,
          통화코드: cost.통화코드,
          환율적용방식: cost.환율적용방식,
          환율적용방식코드: cost.환율적용방식코드,
          적용환율: cost.적용환율,
          성인: cost.성인,
          어린이: cost.어린이,
          영유아: cost.영유아,
          업체번호: cost.업체번호,
        })),
        공용원가: sharedCosts.map((cost) => ({
          원가항목: cost.원가항목,
          통화기호: cost.통화기호,
          통화코드: cost.통화코드,
          환율적용방식: cost.환율적용방식,
          환율적용방식코드: cost.환율적용방식코드,
          적용환율: cost.적용환율,
          성인: cost.성인,
          원가비고: '',
          업체번호: cost.업체번호,
        })),
        수익계산: [
          {
            항목: '인원수',
            번호: 1,
            통화코드: counselingDetail?.통화코드 || '',
            통화기호: counselingDetail?.통화기호 || '',
            성인: counselingDetail?.성인수 || 0,
            어린이: counselingDetail?.소아수 || 0,
            영영유아: counselingDetail?.유아수 || 0,
          },
          {
            항목: '원가',
            번호: 2,
            통화코드: counselingDetail?.통화코드 || '',
            통화기호: counselingDetail?.통화기호 || '',
            성인: calculateTotalCost('성인'),
            어린이: calculateTotalCost('어린이'),
            영유아: calculateTotalCost('영유아'),
          },
          {
            항목: '판매가',
            번호: 3,
            통화코드: counselingDetail?.통화코드 || '',
            통화기호: counselingDetail?.통화기호 || '',
            성인: counselingDetail?.성인판매가 || 0,
            어린이: counselingDetail?.소아판매가 || 0,
            영유아: counselingDetail?.유아판매가 || 0,
          },
          {
            항목: '예상수익',
            번호: 4,
            통화코드: counselingDetail?.통화코드 || '',
            통화기호: counselingDetail?.통화기호 || '',
            성인: calculateProfit('성인'),
            어린이: calculateProfit('어린이'),
            영유아: calculateProfit('영유아'),
          },
        ],
        상담입출금: records.map((record) => ({
          상담번호: counselingDetail?.상담번호 || 0,
          일시: record.일시,
          구분: record.구분,
          입출대상: record.입출대상,
          내역: record.내역,
          금액: record.금액,
        })),
      };

      const postData = [
        {
          컬럼명: '@상담정보',
          값: JSON.stringify(tempData),
        },
        {
          컬럼명: '@처리자',
          값: localStorage.getItem('UserNo'),
        },
        {
          컬럼명: '@업체번호',
          값: localStorage.getItem('CompanyNo'),
        },

        {
          컬럼명: '@예상수익',
          값: counselingDetail?.총예상수익,
        },
        {
          컬럼명: '@견적경로',
          값: counselingDetail?.견적경로,
        },
        {
          컬럼명: '@성인원가',
          값: counselingDetail?.성인원가,
        },
        {
          컬럼명: '@소아원가',
          값: counselingDetail?.소아원가,
        },
        {
          컬럼명: '@유아원가',
          값: counselingDetail?.유아원가,
        },
        {
          컬럼명: '@입금총액',
          값: counselingDetail?.입금총액,
        },
        {
          컬럼명: '@출금총액',
          값: counselingDetail?.출금총액,
        },
        {
          컬럼명: '@총수익',
          값: counselingDetail?.총수익,
        },
        {
          컬럼명: '@고객번호',
          값: selectedCustomer?.고객번호 || initialData?.고객번호 || 0  // 고객번호가 없으면 0으로 저장
        },
      ];
      let procedureName = '';
      if (isOtherCompanyData) {
        procedureName = 'DBO.ITN_T_상담저장_수배처';
      } else {
        procedureName = 'DBO.ITN_T_상담저장';
      }
      const response = await fetchApi<{ Table1: Array<{ 컬럼명: string; 값: string | number }> }>(
        procedureName,
        postData
      );

      if (response) {
        // 성공적으로 저장됨
        // handleClose(true);
        
        onClose(true);
      }
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
    }
  };

  const calculateTotalCost = (type: '성인' | '어린이' | '영유아') => {
    const costs = [...personalCosts, ...sharedCosts];
    return costs.reduce((total, cost) => {
      if (type === '성인') return total + (cost.성인 || 0);
      if (type === '어린이') return total + (cost.어린이 || 0);
      return total + (cost.영유아 || 0);
    }, 0);
  };

  const calculateProfit = (type: '성인' | '어린이' | '영유아') => {
    const cost = calculateTotalCost(type);
    const price =
      type === '성인'
        ? counselingDetail?.성인판매가 || 0
        : type === '어린이'
        ? counselingDetail?.소아판매가 || 0
        : counselingDetail?.유아판매가 || 0;
    return price - cost;
  };

  const handleAssign = () => {
    setShowAssignInfo(true);
  };

  // 수배처 목록 조회
  const fetchSuppliers = async () => {
    try {
      const response = await fetchApi<{ table: Supplier[] }>('ITN_S_수배처조회', [
        { "컬럼명": "@업체번호", "값": localStorage.getItem('CompanyNo') }
      ]);

      if (response.data?.table) {
        setSuppliers(response.data.table);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  // 컴포넌트 마운트 시 수배처 목록 조회
  useEffect(() => {
    fetchSuppliers();
  }, []);

  // 초기 데이터 설정 시 수배처 설정 부분 수정
  useEffect(() => {
    if (counselingDetail?.수배처) {  // initialData가 아닌 counselingDetail의 수배처를 확인
      const supplier = suppliers.find(s => s.업체명 === counselingDetail.수배처);
      setSelectedSupplier(supplier || {
        업체번호: counselingDetail.수배처번호 || 0,
        업체명: counselingDetail.수배처 || ''
      });
    }
  }, [counselingDetail, suppliers]);

  // 수배처 변경 핸들러 수정
  const handleSupplierChange = (event: any, newValue: Supplier | string | null) => {
    if (typeof newValue === 'string') {
      // 직접 입력한 경우
      setCounselingDetail(prev => ({
        ...prev!,
        수배처: newValue,
        수배처번호: null
      }));
      setSelectedSupplier(null);
    } else if (newValue && '업체명' in newValue) {
      // 목록에서 선택한 경우
      setCounselingDetail(prev => ({
        ...prev!,
        수배처: newValue.업체명,
        수배처번호: newValue.업체번호
      }));
      setSelectedSupplier(newValue);
    } else {
      // null인 경우 (선택 해제)
      setCounselingDetail(prev => ({
        ...prev!,
        수배처: '',
        수배처번호: null
      }));
      setSelectedSupplier(null);
    }
  };

  // 수배처 직접 입력 핸들러 수정
  const handleSupplierInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setCounselingDetail(prev => ({
      ...prev!,
      수배처: value,
      수배처번호: null
    }));
  };

  // 수배처 검색 함수 수정
  const handleSupplierSearch = async (searchText: string) => {
    if (searchText.length >= 2) {  // 2글자 이상 입력 시 검색
      try {
        const response = await fetchApi<{ table: CompanyOption[] }>('DBO.ITN_S_공용코드검색', [
          { "컬럼명": "@종류", "값": "업체" },
          { "컬럼명": "@검색어", "값": searchText }
        ]);

        if (response?.data?.table) {
          // CompanyOption을 Supplier 형태로 변환
          const suppliers = response.data.table.map(item => ({
            업체번호: parseInt(item.코드),
            업체명: item.코드명
          }));
          setSuppliers(suppliers);
        }
      } catch (error) {
        console.error('수배처 검색 중 오류:', error);
      }
    }
  };

  if (!open) return null;

  // Dialog 스타일 관련 상수 추가
  const dialogTransition = {
    enter: {
      transform: 'translateX(0%)',
      transition: 'transform 0.3s ease-out',
    },
    exit: {
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease-in',
    },
  };

  const handleCostSave = (calculatedPrices: {
    성인판매가: number;
    소아판매가: number;
    유아판매가: number;
    성인원가: number;
    소아원가: number;
    유아원가: number;
    총원가: CostDetail[];
  }) => {
    if (!counselingDetail) return;

    // 판매총액 계산
    const 판매총액 = 
      calculatedPrices.성인판매가 * counselingDetail.성인수 +
      calculatedPrices.소아판매가 * counselingDetail.소아수 +
      calculatedPrices.유아판매가 * counselingDetail.유아수;

    // 원가총액 계산
    const 원가총액 = 
      calculatedPrices.성인원가 * counselingDetail.성인수 +
      calculatedPrices.소아원가 * counselingDetail.소아수 +
      calculatedPrices.유아원가 * counselingDetail.유아수;

    // 총예상수익 계산
    const 총예상수익 = 판매총액 - 원가총액;

    // counselingDetail 업데이트
    const updatedDetail = {
      ...counselingDetail,
      성인판매가: calculatedPrices.성인판매가,
      소아판매가: calculatedPrices.소아판매가,
      유아판매가: calculatedPrices.유아판매가,
      성인원가: calculatedPrices.성인원가,
      소아원가: calculatedPrices.소아원가,
      유아원가: calculatedPrices.유아원가,
      원가총액,
      판매총액,
      총예상수익,
      총원가: calculatedPrices.총원가 // CostDetail 배열 업데이트
    };

    setCounselingDetail(updatedDetail);
    
    // CostDetail 상태 업데이트
    const personalCosts = calculatedPrices.총원가.filter(cost => !cost.공용원가여부 && !cost.판매가여부);
    const sharedCosts = calculatedPrices.총원가.filter(cost => cost.공용원가여부);
    setPersonalCosts(personalCosts);
    setSharedCosts(sharedCosts);
    
    setIsDataModified(true);
    
    // 상태 업데이트
    set판매총액(판매총액);
    set원가총액(원가총액);
    set성인수익(calculatedPrices.성인판매가 - calculatedPrices.성인원가);
    set소아수익(calculatedPrices.소아판매가 - calculatedPrices.소아원가);
    set유아수익(calculatedPrices.유아판매가 - calculatedPrices.유아원가);
    
    setCostDialogOpen(false);
  };

  // 입출금 다이얼로그 열기 핸들러
  const handlePaymentDialogOpen = () => {
    setRecords(counselingDetail?.입출금 || []);
    setPaymentDialogOpen(true);
  };

  // 입출금 저장 핸들러
  const handlePaymentSave = (입금총액: number, 출금총액: number, records: PaymentRecord[]) => {
    if (!counselingDetail) return;

    // 상태 업데이트를 위한 새로운 객체 생성
    const updatedDetail = {
      ...counselingDetail,
      입금총액,
      출금총액,
      입출금: records,
    };

    // 재계산 실행
    const recalculatedDetail = recalculateAll(updatedDetail);

    // 상태 업데이트
    setCounselingDetail(recalculatedDetail as CounselingDetail);
    setRecords(records);
    setIsDataModified(true);
    setPaymentDialogOpen(false);
  };

  // 세부원가 다이얼로그 열기 핸들러
  const handleCostDialogOpen = () => {
    if (counselingDetail) {
      // 총원가 데이터에서 판매가가 아닌 항목들만 필터링
      let costs = counselingDetail.총원가 || [];

      // 판매가가 아닌 항목들 중에서 개인별/공용 원가 분리
      const personalCosts = costs.filter((cost) => !cost.공용원가여부 && !cost.판매가여부);
      const sharedCosts = costs.filter((cost) => cost.공용원가여부 && !cost.판매가여부);

      setPersonalCosts(personalCosts);
      setSharedCosts(sharedCosts);
    }
    setCostDialogOpen(true);
  };

  const handleCostDialogClose = () => {
    setCostDialogOpen(false);
  };

  // 백드롭 클릭 핸들러 추가
  const handleClose = (refresh: boolean = false) => {
    if (isDataModified) {
      setPendingClose(refresh);
      setConfirmDialogOpen(true);
    } else {
      onClose(refresh);
    }
  };

  const handleDialogClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    // ESC 키나 backdrop 클릭 시
    handleClose(false);
  };

  // 검색 함수 추가
  const searchCompanies = async (searchValue: string) => {
    try {
      const response = await fetchApi<{ table: CompanyOption[] }>('DBO.ITN_S_공용코드검색', [
        { 컬럼명: '@종류', 값: '업체' },
        { 컬럼명: '@검색어', 값: searchValue },
      ]);

      if (response?.data?.table) {
        setCompanyOptions(response.data.table);
      }
    } catch (error) {
      console.error('업체 검색 중 오류:', error);
    }
  };

  // 확인 다이얼로그 처리 함수 추가
  const handleConfirmClose = () => {
    setConfirmDialogOpen(false);
    onClose(pendingClose);
  };

  // 전화번호 포맷 제거 함수 추가
  const removePhoneFormat = (phone: string) => {
    return phone.replace(/-/g, '');
  };

  // 고객 검색 함수 수정
  const searchCustomers = async (searchValue: string, searchType: '고객명' | '대표전화') => {
    try {
      if (!searchValue) {
        setCustomerOptions([]);
        return;
      }

      if (searchType === '대표전화') {
        const cleanPhone = removePhoneFormat(searchValue);
        if (cleanPhone.length < 6) {
          setCustomerOptions([]);
          return;
        }
        searchValue = cleanPhone;
      } else if (searchValue.length < 2) { // 2글자로 변경
        setCustomerOptions([]);
        return;
      }

      const postData = [
        { 컬럼명: '@업체번호', 값: localStorage.getItem('CompanyNo') },
        { 컬럼명: searchType === '고객명' ? '@고객명' : '@대표전화', 값: searchValue }
      ];

      const response = await fetchApi<{ table: CustomerOption[] }>('ITN_S_간편일정고객조회', postData);

      if (response?.data?.table) {
        setCustomerOptions(response.data.table);
      }
    } catch (error) {
      console.error('고객 검색 중 오류:', error);
      setCustomerOptions([]);
    }
  };

  // 요청자 정보 입력 필드 수정 부분
  // Grid container 내부의 해당 부분을 찾아 다음과 같이 수정:
  {!isOtherCompanyData && (
    <>
      <Grid item xs={isMobile ? 12 : 1.5}>
        <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>요청자명</Typography>
        <StyledTextField
          value={counselingDetail?.요청자명 || ''}
          onChange={(e) => {
            handleInputChange('요청자명', e.target.value);
            if (e.target.value.length >= 2) {
              searchCustomers(e.target.value, '고객명');
            } else {
              setCustomerOptions([]);
            }
          }}
          onFocus={() => setActiveField('고객명')}
          onBlur={() => setTimeout(() => setActiveField(null), 200)}
          placeholder="2글자 이상 입력"
        />
        {activeField === '고객명' && customerOptions.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              zIndex: 1000,
              width: '500px',
              mt: 0.5,
              boxShadow: 3,
            }}
          >
            {customerOptions.map((option, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid #eee'
                }}
                onClick={() => {
                  setSelectedCustomer(option);
                  handleInputChange('요청자명', option.고객명);
                  handleInputChange('요청자이메일', option.이메일);
                  handleInputChange('요청자휴대폰', option.대표전화);
                  if (initialData) {
                    initialData.고객번호 = option.고객번호;
                  }
                  setCustomerOptions([]);
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {option.고객명}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {option.대표전화} | {option.이메일}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Grid>
      <Grid item xs={isMobile ? 12 : 2}>
        <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>요청자휴대폰</Typography>
        <StyledTextField
          value={counselingDetail?.요청자휴대폰 || ''}
          onChange={(e) => {
            handleInputChange('요청자휴대폰', e.target.value);
            const cleanPhone = removePhoneFormat(e.target.value);
            if (cleanPhone.length >= 6) {
              searchCustomers(cleanPhone, '대표전화');
            } else {
              setCustomerOptions([]);
            }
          }}
          onFocus={() => setActiveField('대표전화')}
          onBlur={() => setTimeout(() => setActiveField(null), 200)}
          placeholder="전화번호 6자리 이상 입력"
        />
        {activeField === '대표전화' && customerOptions.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              zIndex: 1000,
              width: '500px',
              mt: 0.5,
              boxShadow: 3,
            }}
          >
            {customerOptions.map((option, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid #eee'
                }}
                onClick={() => {
                  setSelectedCustomer(option);
                  handleInputChange('요청자명', option.고객명);
                  handleInputChange('요청자이메일', option.이메일);
                  handleInputChange('요청자휴대폰', option.대표전화);
                  if (initialData) {
                    initialData.고객번호 = option.고객번호;
                  }
                  setCustomerOptions([]);
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {option.대표전화}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {option.고객명} | {option.이메일}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Grid>
    </>
  )}

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose} // 타입에 맞는 핸들러 사용
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            position: 'fixed',
            right: 0,
            m: 0,
            height: '100%',
            width: isMobile ? '100%' : '1600px',
            maxHeight: '100vh',
            borderRadius: 0,
          },
        }}
        onClick={(e) => {
          // Dialog 영역 밖 클릭 감지
          if (e.target === e.currentTarget) {
            handleClose(false);
          }
        }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: 'primary.main',
              color: 'white',
            }}>
            <Typography variant="h6" sx={{ flex: 1, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              상담 상세
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {/* <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={handleDelete}
                              sx={{
                                  fontSize: '13px',
                                  height: '36px',
                                  bgcolor: 'error.main'
                              }}
                          >
                              상담취소
                          </Button> */}
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                sx={{
                  fontSize: isMobile ? '12px' : '13px',
                  height: isMobile ? '32px' : '36px',
                  bgcolor: 'info.main',
                  width: isMobile ? '100px' : '120px',
                }}>
                저장
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleClose(false)}
                sx={{
                  fontSize: isMobile ? '12px' : '13px',
                  height: isMobile ? '32px' : '36px',
                  bgcolor: 'grey.600',
                  width: isMobile ? '100px' : '120px',
                }}>
                닫기
              </Button>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
            ) : (
              counselingDetail && (
                <Grid container spacing={2} sx={{ p: 2 }}>
                  <Grid item xs={isMobile ? 12 : 8}>
                    <StyledPaper sx={{ p: 3, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={isMobile ? 12 : 8}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>견적명</Typography>
                          <StyledTextField
                            value={counselingDetail.견적명}
                            onChange={(e) => handleInputChange('견적명', e.target.value)}
                            disabled={isOtherCompanyData}
                          />
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 4}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>여행 기간</Typography>
                          <DatePickerWrapper>
                            <DatePicker
                              disabled={isOtherCompanyData}
                              selectsRange
                              monthsShown={isMobile ? 1 : 2}
                              startDate={dateRange[0]} // counselingDetail.시작일 대신 dateRange 사용
                              endDate={dateRange[1]} // counselingDetail.종료일 대신 dateRange 사용
                              onChange={(update: [Date | null, Date | null]) => {
                                const [start, end] = update;
                                setDateRange([start, end]); // dateRange 상태 업데이트

                                // counselingDetail 업데이트
                                handleInputChange('시작일', start ? formatDate(start) : '');
                                handleInputChange('종료일', end ? formatDate(end) : '');

                                // 포커스 해제
                                if (end || (!start && !end)) {
                                  setTimeout(() => {
                                    const element = document.querySelector('.react-datepicker__input-container input');
                                    if (element) {
                                      (element as HTMLElement).blur();
                                    }
                                  }, 100);
                                }
                              }}
                              dateFormat="yyyy/MM/dd"
                              locale={ko}
                              minDate={new Date()}
                              placeholderText="날짜를 선택하세요"
                              isClearable={true}
                              showPopperArrow={false}
                              calendarStartDay={1}
                              customInput={<StyledTextField fullWidth size="small" />}
                            />
                          </DatePickerWrapper>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 5}>
                          <Stack direction="row" spacing={2}>
                            <Box sx={{ width: '23%' }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>성인</Typography>
                              <StyledTextField
                                type="number"
                                value={counselingDetail.성인수}
                                onChange={(e) => handleInputChange('성인수', e.target.value)}
                                disabled={isOtherCompanyData}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'right' },
                                }}
                              />
                            </Box>
                            <Box sx={{ width: '23%' }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>소아</Typography>
                              <StyledTextField
                                type="number"
                                value={counselingDetail.소아수}
                                onChange={(e) => handleInputChange('소아수', e.target.value)}
                                disabled={isOtherCompanyData}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'right' },
                                }}
                              />
                            </Box>
                            <Box sx={{ width: '23%' }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>유아</Typography>
                              <StyledTextField
                                type="number"
                                value={counselingDetail.유아수}
                                onChange={(e) => handleInputChange('유아수', e.target.value)}
                                disabled={isOtherCompanyData}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'right' },
                                }}
                              />
                            </Box>
                            <Box sx={{ width: '23%' }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>인솔자</Typography>
                              <StyledTextField
                                type="number"
                                value={counselingDetail.인솔자수}
                                onChange={(e) => handleInputChange('인솔자수', e.target.value)}
                                disabled={isOtherCompanyData}
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: 'right' },
                                }}
                              />
                            </Box>
                          </Stack>
                        </Grid>
                        {!isOtherCompanyData && (
                          <>
                            <Grid item xs={isMobile ? 12 : 1.5}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>요청자명</Typography>
                              <StyledTextField
                                value={counselingDetail?.요청자명 || ''}
                                onChange={(e) => {
                                  handleInputChange('요청자명', e.target.value);
                                  if (e.target.value.length >= 2) {
                                    searchCustomers(e.target.value, '고객명');
                                  } else {
                                    setCustomerOptions([]);
                                  }
                                }}
                                onFocus={() => setActiveField('고객명')}
                                onBlur={() => setTimeout(() => setActiveField(null), 200)}
                                placeholder="2글자 이상 입력"
                              />
                              {activeField === '고객명' && customerOptions.length > 0 && (
                                <Paper
                                  sx={{
                                    position: 'absolute',
                                    zIndex: 1000,
                                    width: '500px',
                                    mt: 0.5,
                                    boxShadow: 3,
                                  }}
                                >
                                  {customerOptions.map((option, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        borderBottom: '1px solid #eee'
                                      }}
                                      onClick={() => {
                                        setSelectedCustomer(option);
                                        handleInputChange('요청자명', option.고객명);
                                        handleInputChange('요청자이메일', option.이메일);
                                        handleInputChange('요청자휴대폰', option.대표전화);
                                        if (initialData) {
                                          initialData.고객번호 = option.고객번호;
                                        }
                                        setCustomerOptions([]);
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {option.고객명}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        {option.대표전화} | {option.이메일}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Paper>
                              )}
                            </Grid>
                            <Grid item xs={isMobile ? 12 : 2}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>요청자휴대폰</Typography>
                              <StyledTextField
                                value={counselingDetail?.요청자휴대폰 || ''}
                                onChange={(e) => {
                                  handleInputChange('요청자휴대폰', e.target.value);
                                  const cleanPhone = removePhoneFormat(e.target.value);
                                  if (cleanPhone.length >= 6) {
                                    searchCustomers(cleanPhone, '대표전화');
                                  } else {
                                    setCustomerOptions([]);
                                  }
                                }}
                                onFocus={() => setActiveField('대표전화')}
                                onBlur={() => setTimeout(() => setActiveField(null), 200)}
                                placeholder="전화번호 6자리 이상 입력"
                              />
                              {activeField === '대표전화' && customerOptions.length > 0 && (
                                <Paper
                                  sx={{
                                    position: 'absolute',
                                    zIndex: 1000,
                                    width: '500px',
                                    mt: 0.5,
                                    boxShadow: 3,
                                  }}
                                >
                                  {customerOptions.map((option, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        borderBottom: '1px solid #eee'
                                      }}
                                      onClick={() => {
                                        setSelectedCustomer(option);
                                        handleInputChange('요청자명', option.고객명);
                                        handleInputChange('요청자이메일', option.이메일);
                                        handleInputChange('요청자휴대폰', option.대표전화);
                                        if (initialData) {
                                          initialData.고객번호 = option.고객번호;
                                        }
                                        setCustomerOptions([]);
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {option.대표전화}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        {option.고객명} | {option.이메일}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Paper>
                              )}
                            </Grid>
                          </>
                        )}
                        {!isOtherCompanyData && (
                          <Grid item xs={isMobile ? 12 : 3.5}>
                            <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>요청자이메일</Typography>
                            <StyledTextField
                              value={counselingDetail.요청자이메일}
                              onChange={(e) => handleInputChange('요청자이메일', e.target.value)}
                              disabled={isOtherCompanyData}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </StyledPaper>
                    <StyledPaper sx={{ p: 3, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={isMobile ? 6 : 1.5}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>상담구분</Typography>
                          <StyledTextField
                            select
                            value={counselingDetail.상담구분}
                            onChange={(e) => handleInputChange('상담구분', e.target.value)}
                            disabled={isOtherCompanyData}>
                            <MenuItem value="419001">맞춤여행</MenuItem>
                            <MenuItem value="419002">외부상품조인</MenuItem>
                          </StyledTextField>
                        </Grid>
                        <Grid item xs={isMobile ? 6 : 1.5}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>상담상태</Typography>
                          <StyledTextField
                            select
                            value={counselingDetail.상담상태}
                            onChange={(e) => handleInputChange('상담상태', e.target.value)}
                            disabled={isOtherCompanyData}>
                            <MenuItem value="420003">상담요청</MenuItem>
                            <MenuItem value="420001">진행중</MenuItem>
                            <MenuItem value="420002">예약전환</MenuItem>
                            <MenuItem value="420999">상담취소</MenuItem>
                          </StyledTextField>
                        </Grid>
                        <Grid item xs={isMobile ? 8 : 2.5}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>수배처</Typography>
                          <Autocomplete
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            options={suppliers}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return option?.업체명 || '';
                            }}
                            isOptionEqualToValue={(option, value) => 
                              option.업체명 === value.업체명 || option.업체번호 === value.업체번호
                            }
                            freeSolo
                            onInputChange={(event, newInputValue) => {
                              if (newInputValue.length >= 2) {
                                handleSupplierSearch(newInputValue);
                              } else {
                                setSuppliers([]); // 2글자 미만일 때는 목록 비우기
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                onChange={handleSupplierInputChange}
                                placeholder="업체명을 입력하세요 (2글자 이상)"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    height: '33px'
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={isMobile ? 4 : 1.5}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>담당자</Typography>
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ height: '32px', width: '100%', fontSize: '14px', borderRadius: '4px' }}
                            onClick={handleAssign}
                            disabled={!isOtherCompanyData}
                          >
                            {counselingDetail.담당자}
                          </Button>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 5}>
                          <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>일정표 주소 </Typography>
                          <StyledTextField
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      // window.open(
                                      //     `${counselingDetail.일정표주소}`,
                                      //     '_blank',
                                      //     `width=1600px,height=960px`
                                      // );
                                      // const previewUrl = `${window.location.origin}/preview/${counselingDetail.일정표주소.replace(/\\/g, '')}`;
                                      const previewUrl = counselingDetail.일정표주소;
                                      window.open(previewUrl, '_blank');
                                    }}>
                                    <OpenInNewIcon sx={{ color: '#666666', fontSize: 20 }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{ width: '100%' }}
                            placeholder="https:// "
                            value={counselingDetail.일정표주소}
                            onChange={(e) => handleInputChange('일정표주소', e.target.value)}
                            disabled={isOtherCompanyData}></StyledTextField>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 12}>
                          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ alignItems: 'stretch' }}>
                            <Box sx={{ width: isMobile ? '100%' : '50%', display: 'flex', flexDirection: 'column' }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>견적내용</Typography>
                              <StyledTextField
                                multiline
                                minRows={4}
                                maxRows={20}
                                value={counselingDetail.견적내용}
                                onChange={(e) => handleInputChange('견적내용', e.target.value)}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    padding: 0,
                                    lineHeight: 1.5,
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: '8px 16px',
                                    lineHeight: '20px',
                                    overflow: 'auto !important',
                                    fontSize: '13px !important',
                                  },
                                }}
                                disabled={isOtherCompanyData}
                              />
                            </Box>
                            <Box sx={{ width: isMobile ? '100%' : '50%', display: 'flex', flexDirection: 'column', mt: isMobile ? 2 : 0 }}>
                              <Typography sx={{ fontSize: '12px', color: '#9a9a9a', mb: 0.5 }}>
                                고객명단 (자유형식으로 입력)
                              </Typography>
                              <StyledTextField
                                multiline
                                minRows={4}
                                maxRows={20}
                                value={counselingDetail.고객명단}
                                onChange={(e) => handleInputChange('고객명단', e.target.value)}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    padding: 0,
                                    lineHeight: 1.5,
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: '8px 16px',
                                    lineHeight: '20px',
                                    overflow: 'auto !important',
                                    fontSize: '13px !important',
                                  },
                                }}
                                disabled={isOtherCompanyData}
                              />
                            </Box>
                          </Stack>
                        </Grid>

                        <Stack direction="row" spacing={1} sx={{ mt: 2, ml: 1, pl: 2 }}>
                          <Typography sx={{ width: '80px', fontSize: '14px', height: '32px' }}>첨부파일</Typography>
                          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1, ml: 1 }}>
                            {attachedFiles.map((file, index) => (
                              <Button
                                key={index}
                                variant="outlined"
                                size="small"
                                sx={{ height: '32px', fontSize: '12px' }}
                                onClick={() => window.open(file.첨부파일경로, '_blank')}>
                                <Typography noWrap sx={{ maxWidth: '130px', fontSize: '11px', textTransform: 'none' }}>
                                  {file.첨부파일}
                                </Typography>
                              </Button>
                            ))}
                          </Stack>
                        </Stack>
                      </Grid>
                    </StyledPaper>
                    {/* 세부원가 설정 - 모바일 */}
                    {isMobile ? (
                      <Stack spacing={2}>
                        {/* 버튼 그룹 */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                          <Button
                            variant="contained"
                            onClick={handleCostDialogOpen}
                            
                            sx={{ minWidth: '140px', width : '50%', height: '32px' }}
                          >
                            판매가/원가 설정
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handlePaymentDialogOpen}
                            
                            sx={{ minWidth: '140px', width : '50%', height: '32px' }}
                          >
                            입금/출금
                          </Button>
                        </Box>

                        {/* 성인 카드 */}
                        <StyledPaper sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>성인</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>판매가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#1976d2',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.성인판매가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>원가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#d32f2f',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.성인원가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>예상수익</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: 'text.primary',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(성인수익)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </StyledPaper>

                        {/* 소아 카드 */}
                        <StyledPaper sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>소아</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>판매가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#1976d2',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.소아판매가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>원가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#d32f2f',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.소아원가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>예상수익</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: 'text.primary',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(소아수익)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </StyledPaper>

                        {/* 영유아 카드 */}
                        <StyledPaper sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>영유아</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>판매가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#1976d2',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.유아판매가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>원가</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#d32f2f',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.유아원가)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>예상수익</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: 'text.primary',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(유아수익)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </StyledPaper>

                        {/* 합계 카드 */}
                        <StyledPaper sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>합계</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>판매 총액</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#1976d2',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(판매총액)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>원가 총액</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: '#d32f2f',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(원가총액)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>총 예상수익</Typography>
                              <Typography 
                                sx={{ 
                                  mt: 0.5, 
                                  color: 'text.primary',
                                  fontSize: '14px',
                                  textAlign: 'center'
                                }}
                              >
                                {formatNumberWithCommas(counselingDetail.총예상수익)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </StyledPaper>
                      </Stack>
                    ) : (
                      <StyledPaper sx={{ px: 2, py: 1, pr: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCostDialogOpen}
                            sx={{ height: '32px', fontSize: '13px' , px: 2.5}}>
                            판매가/원가 설정
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handlePaymentDialogOpen}
                            sx={{ height: '32px', fontSize: '13px' , px: 2.5}}>
                            입금/출금
                          </Button>

                        </Box>
                        <TableContainer sx={{ height: '150px' }}>
                          <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '6px 6px' } }}>
                            <TableHead>
                              <TableRow sx={{ height: '32px', borderTop: 1, borderColor: 'divider', whiteSpace: 'nowrap' }}>
                                <TableCell sx={{ width: '9%', minWidth: '60px', whiteSpace: 'nowrap', paddingRight: 0 }}>
                                  항목
                                </TableCell>
                                <TableCell align="center" sx={{ width: '13%', whiteSpace: 'nowrap', paddingcenter: 0, borderLeft: 1, borderColor: 'divider' }}>
                                  성인
                                </TableCell>
                                <TableCell align="center" sx={{ width: '12%', whiteSpace: 'nowrap', borderLeft: 1, borderColor: 'divider' }}>
                                  소아
                                </TableCell>
                                <TableCell align="center" sx={{ width: '12%', whiteSpace: 'nowrap', borderLeft: 1, borderColor: 'divider' }}>
                                  영유아
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ width: '14%', borderLeft: 1, borderColor: 'divider', whiteSpace: 'nowrap' }}>
                                  합계
                                </TableCell>
                                <TableCell
                                  align="center"
                                  colSpan={2}
                                  sx={{ width: '20%', borderLeft: 1, borderColor: 'divider', whiteSpace: 'nowrap' }}>
                                  입금/출금
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ width: '20%', borderLeft: 1, borderColor: 'divider', whiteSpace: 'nowrap' }}
                                  colSpan={2}>
                                  미수/미지급
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell>판매가</TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.성인판매가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.소아판매가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.유아판매가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(판매총액)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                    
                                  }}>
                                  입금
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.입금총액 ?? '')}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}>
                                  미수
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(미수)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>원가</TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.성인원가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.소아원가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.유아원가)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(원가총액)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}>
                                  지급
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.출금총액)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}>
                                  미지급
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(미지급)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>예상수익</TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(성인수익)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(소아수익)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(유아수익)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.총예상수익)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}>
                                  차액
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(입금차액)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    width: '80px',
                                    fontSize: '12px',
                                    color: '#ccc',
                                    borderLeft: 1,
                                    borderColor: 'divider',
                                  }}>
                                  실수익
                                </TableCell>
                                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                                  <Typography sx={{ fontSize: '14px', mr: 1 }}>
                                    {formatNumberWithCommas(counselingDetail.총수익)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </StyledPaper>
                    )}
                  </Grid>

                  <Grid item xs={isMobile ? 12 : 4}>
                    <StyledPaper
                      variant="outlined"
                      sx={{
                        height: 'calc(100vh - 100px)',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.10), 0 2px 10px rgba(0,0,0,0.03)',
                        // overflow: 'hidden'
                      }}>
                      <Box
                        sx={{
                          flex: 1,
                          borderRight: 1,
                          borderColor: 'divider',
                          overflow: 'auto',
                        }}>
                        <CounselingRecordDialog
                          counselingNo={counselingNo}
                          open={true}
                          onClose={() => {}}
                          onFileAttached={(file) => {
                            setAttachedFiles((prev) => [
                              ...prev,
                              {
                                첨부파일: file.fileName,
                                첨부파일경로: file.filePath,
                              },
                            ]);
                          }}
                        />
                      </Box>
                    </StyledPaper>
                  </Grid>
                </Grid>
              )
            )}
          </DialogContent>

          {/* 세부원가 설정 다이얼로그 */}
          {costDialogOpen && counselingDetail && (
            <CostManageDialog
              open={costDialogOpen}
              onClose={handleCostDialogClose}
              onSave={(calculatedPrices) => {
                handleCostSave(calculatedPrices);
                setCostDialogOpen(false);
              }}
              initialPersonalCosts={personalCosts}
              initialSharedCosts={sharedCosts}
              counselingDetail={counselingDetail}
              setCounselingDetail={setCounselingDetail}
              setIsDataModified={setIsDataModified}
              isOtherCompanyData={isOtherCompanyData}
              companyNo={localStorage.getItem('CompanyNo')}
            />
          )}

          {/* 입출금 관리 다이얼로그 */}
          {counselingDetail && (
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="md" fullWidth>
              <PaymentManageDialog
                open={true}
                onClose={() => setPaymentDialogOpen(false)}
                onSave={handlePaymentSave}
                initialRecords={records}
              />
            </Dialog>
          )}

          {counselingDetail && (
            <AssignInfoDialog
              open={showAssignInfo}
              onClose={() => setShowAssignInfo(false)}
              counselingDetail={counselingDetail}
            />
          )}

          {/* 확인 다이얼로그 */}
          <ConfirmDialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={handleConfirmClose}
            message="변경사항이 있습니다. 저장하지 않고 닫으시겠습니까?"
          />
        </Box>
      </Dialog>

      {/* 고객 자동 등록 확인 다이얼로그 */}
      <Dialog
        open={autoRegisterConfirmOpen}
        onClose={() => setAutoRegisterConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          고객 자동 등록
          <IconButton 
            onClick={() => setAutoRegisterConfirmOpen(false)}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            상담등록과 함께 입력하신 요청자 정보로 고객을 자동 등록하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setAutoRegisterConfirmOpen(false);
              handleCustomerAutoRegister();
            }}
            variant="contained"
            color="primary"
            sx={{ mr: 1 }}
          >
            확인
          </Button>
          <Button 
            onClick={() => {
              setAutoRegisterConfirmOpen(false);
              // 고객번호를 0으로 설정하고 상담 저장 진행
              if (initialData) {
                initialData.고객번호 = 0;
              }
              setSelectedCustomer(null);
              if (counselingDetail) {
                setCounselingDetail({
                  ...counselingDetail,
                  고객번호: 0
                });
              }
              handleSave(true);  // skipCustomerCheck를 true로 설정하여 고객 체크를 건너뛰고 저장
            }}
            variant="contained"
            color="inherit"
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CounselingDetailDialog;